import { useEffect, useRef } from "react";
import { useReducedMotion } from "framer-motion";
import { isVeryLowPower } from "./perf";
import { getTempo } from "./tempo";

// Cursor proximity "gel press" — elements gently bend toward the cursor and
// sink (scale + translateZ feel) as it approaches, then ease back. This is
// the brand's signature tactility: one physics, applied everywhere.
//
// Performance: a single global listener set, cached rects, one rAF loop that
// self-terminates when everything settles. Pointer-fine devices only.

const els = new Set();
const rects = new Map();
const cur = new Map();
let mx = -9999;
let my = -9999;
let raf = 0;
let dirty = true;
let bound = false;
let lastScroll = -1;
const RADIUS = 150;

const frame = () => {
  raf = 0;
  const scrollY = window.scrollY;
  if (scrollY !== lastScroll) {
    lastScroll = scrollY;
    dirty = true;
  }
  if (dirty) {
    els.forEach((el) => rects.set(el, el.getBoundingClientRect()));
    dirty = false;
  }
  let alive = false;
  els.forEach((el) => {
    const r = rects.get(el);
    if (!r) return;
    const dx = mx - (r.left + r.width / 2);
    const dy = my - (r.top + r.height / 2);
    const target = Math.max(0, 1 - Math.hypot(dx, dy) / RADIUS);
    const prev = cur.get(el) || 0;
    const next = prev + (target - prev) * (0.16 / getTempo());
    // Coupled settle+clear: once the cursor is gone, decay always runs to
    // a full clear — never strand a residual transform on the element.
    if (target === 0 && (next < 0.02 || Math.abs(next - prev) < 0.0006)) {
      if (cur.has(el)) {
        cur.delete(el);
        el.style.transform = "";
      }
      return;
    }
    if (Math.abs(next - prev) > 0.0006 || dirty) alive = true;
    cur.set(el, next);
    const rx = (-dy / RADIUS) * 3 * next;
    const ry = (dx / RADIUS) * 3 * next;
    el.style.transform = `perspective(750px) translateY(${(3 * next).toFixed(2)}px) scale(${(1 - 0.018 * next).toFixed(4)}) rotateX(${rx.toFixed(2)}deg) rotateY(${ry.toFixed(2)}deg)`;
  });
  if (alive) schedule();
};

const schedule = () => {
  if (!raf) raf = requestAnimationFrame(frame);
};

const bind = () => {
  if (bound || typeof window === "undefined") return;
  bound = true;
  window.addEventListener(
    "mousemove",
    (e) => {
      mx = e.clientX;
      my = e.clientY;
      schedule();
    },
    { passive: true }
  );
  window.addEventListener(
    "scroll",
    () => {
      dirty = true;
      schedule();
    },
    { passive: true, capture: true }
  );
  window.addEventListener(
    "resize",
    () => {
      dirty = true;
      schedule();
    },
    { passive: true }
  );
  document.documentElement.addEventListener("mouseleave", () => {
    mx = -9999;
    my = -9999;
    schedule();
  });
};

export const registerSink = (el) => {
  bind();
  els.add(el);
  dirty = true;
  schedule();
  return () => {
    els.delete(el);
    rects.delete(el);
    cur.delete(el);
    el.style.transform = "";
  };
};

// Attach to any element to make it bend & sink near the cursor.
export const useSinkRef = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  useEffect(() => {
    const el = ref.current;
    if (!el || reduce || isVeryLowPower()) return undefined;
    if (!window.matchMedia("(pointer: fine)").matches) return undefined;
    return registerSink(el);
  }, [reduce]);
  return ref;
};
